import { LoginRequest, RegisterRequest, LoginResponse, RegisterResponse } from '../domain/types';
import { API_CONFIG, AUTH_API_BASE_URL } from '../../../shared/api/config';

const API_BASE_URL = API_CONFIG.baseURL;

export class AuthApi {
  private extractAccessToken(data: any): string | null {
    const token =
      data?.access_token ||
      data?.accessToken ||
      data?.token ||
      data?.tokens?.access ||
      null;

    if (typeof token !== 'string' || !token.trim()) {
      return null;
    }

    return token.trim();
  }

  async getHudleSessionAccessToken(): Promise<{ success: boolean; accessToken?: string; error?: string; errorCode?: string; status?: number }> {
    try {
      const response = await fetch(`${AUTH_API_BASE_URL}/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      const data = await response.json().catch(() => ({}));
      const accessToken = this.extractAccessToken(data);

      if (!response.ok || !accessToken) {
        return {
          success: false,
          error: data?.error || data?.detail || 'Сессия приложения не найдена',
          errorCode: data?.error_code,
          status: response.status,
        };
      }

      return { success: true, accessToken };
    } catch {
      return {
        success: false,
        error: 'Ошибка подключения к auth-service',
        errorCode: 'auth_service_unreachable',
        status: 401,
      };
    }
  }

  async sessionLogin(externalAccessToken: string): Promise<LoginResponse & { status?: number; error_code?: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/session-login/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${externalAccessToken}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        return {
          success: false,
          error: data?.error || data?.detail || 'Ошибка SSO авторизации',
          error_code: data?.error_code,
          status: response.status,
        };
      }

      return {
        ...data,
        success: true,
        status: response.status,
      };
    } catch {
      return {
        success: false,
        error: 'Ошибка подключения к серверу',
        error_code: 'crm_backend_unreachable',
      };
    }
  }

  async login(request: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.detail || 'Ошибка авторизации',
        };
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: 'Ошибка подключения к серверу',
      };
    }
  }

  async register(request: RegisterRequest): Promise<RegisterResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.detail || 'Ошибка регистрации',
        };
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: 'Ошибка подключения к серверу',
      };
    }
  }

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.detail || 'Ошибка обновления токена',
        };
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: 'Ошибка обновления токена',
      };
    }
  }

  async logout(refreshToken: string): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/auth/logout/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch (error) {
    }
  }
}
